import { SupportTicketPriority } from "@redotech/crdb-enums/shopper-ai/support-ticket-priority";
import { SupportTicketSnoozeDuration } from "@redotech/crdb-enums/shopper-ai/support-ticket-snooze-duration";
import * as z from "zod";

import type { Contract, Target } from "../types";

// Input universe for support.custom-action: a JSON snapshot of the affected
// ticket (+ threads + trigger) projected from the rule engine's context.

const ticketSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  // SupportTicketStatus (OPEN | CLOSED | SNOOZED | IN_PROGRESS | ARCHIVED | ...).
  status: z.string(),
  // "URGENT" or null.
  priority: z.string().nullable(),
  // "USER" | "AI" | null.
  assigneeType: z.string().nullable(),
  mongoAssigneeId: z.string().nullable(),
  mongoCustomerId: z.string().nullable(),
  ticketNumber: z.number().nullable(),
  closureReason: z.string().nullable(),
  // ISO-8601 timestamps (null when unset), so they cross the V8 boundary.
  readAt: z.string().nullable(),
  lastCustomerMessageAt: z.string().nullable(),
  lastMerchantMessageAt: z.string().nullable(),
  statusUpdatedAt: z.string().nullable(),
  snoozedUntil: z.string().nullable(),
});

const threadSchema = z.object({
  id: z.string(),
  // MessageChannel (email | sms | voice | ...).
  channel: z.string(),
  // Integration type (gmail | outlook | redoChatFlow | ...).
  integrationType: z.string(),
  integrationId: z.string(),
  externalThreadId: z.string(),
  createdAt: z.string(),
  // Present for email threads only.
  emailSubject: z.string().nullable().optional(),
});

const triggerSchema = z.object({
  // RuleTriggerType that fired the rule (e.g. CONVERSATION_STATUS_CHANGED).
  triggerType: z.string(),
  triggeredAt: z.string(),
  subjectEntityId: z.string(),
});

export const supportCustomActionInputSchema = z.object({
  ticket: ticketSchema,
  threads: z.array(threadSchema),
  channels: z.object({
    integrationTypes: z.array(z.string()),
    labels: z.array(z.string()),
  }),
  trigger: triggerSchema,
});

export type SupportCustomActionInput = z.infer<
  typeof supportCustomActionInputSchema
>;

// Output: actions the engine runs after the artifact returns. Each variant
// mirrors a rule-action config so the handler reuses the existing handlers.

const addTagAction = z.object({
  kind: z.literal("addTag"),
  tags: z.array(z.string().min(1)).min(1),
});

const removeTagAction = z.object({
  kind: z.literal("removeTag"),
  tags: z.array(z.string().min(1)).min(1),
});

const setStatusAction = z.object({
  kind: z.literal("setStatus"),
  status: z.enum([
    "OPEN",
    "CLOSED",
    "SNOOZED",
    "IN_PROGRESS",
    "ARCHIVED",
    "DELETED",
  ]),
  snoozeDuration: z.enum(SupportTicketSnoozeDuration).optional(),
});

const setPriorityAction = z.object({
  kind: z.literal("setPriority"),
  priority: z.enum(SupportTicketPriority).nullable(),
});

const autoReplyAction = z.object({
  kind: z.literal("autoReply"),
  textContent: z.string(),
  htmlContent: z.string(),
});

export const supportCustomActionSchema = z.union([
  addTagAction,
  removeTagAction,
  setStatusAction,
  setPriorityAction,
  autoReplyAction,
]);

export type SupportCustomAction = z.infer<typeof supportCustomActionSchema>;

export const supportCustomActionOutputSchema = z.object({
  actions: z.array(supportCustomActionSchema),
});

export type SupportCustomActionOutput = z.infer<
  typeof supportCustomActionOutputSchema
>;

export const supportCustomActionTarget = {
  id: "support.custom-action",
  displayName: "Support custom action",
  description:
    "A code-authored support-ticket rule action. Receives the affected ticket (narrowed by the step's selector) and returns a sequence of actions to run.",
} as const satisfies Target;

export const supportCustomActionStarterCode = `// Edit this function. It receives the contract's Input and returns Output.
// Runs with fetch enabled, so you can call a webhook / report / model API.
export default async (input: Input): Promise<Output> => {
  return { actions: [] };
};
`;

// Ambient `fetch` surface so upload-time type-checking matches the deno_fetch
// globals the RedoJS runtime injects when includeFetch is set.
const fetchRuntimePreludeDts = `
type HeadersInit = Record<string, string> | [string, string][];
interface Headers {
  get(name: string): string | null;
  has(name: string): boolean;
  forEach(callback: (value: string, key: string) => void): void;
}
interface RequestInit {
  method?: string;
  headers?: HeadersInit;
  body?: string;
}
interface Response {
  readonly ok: boolean;
  readonly status: number;
  readonly statusText: string;
  readonly url: string;
  readonly headers: Headers;
  json(): Promise<any>;
  text(): Promise<string>;
}
declare function fetch(url: string, init?: RequestInit): Promise<Response>;
`;

export const supportCustomActionContractV1: Contract = {
  id: "support.custom-action@2026-06.1",
  targetId: supportCustomActionTarget.id,
  inputSchema: supportCustomActionInputSchema,
  outputSchema: supportCustomActionOutputSchema,
  includeFetch: true,
  runtimePreludeDts: fetchRuntimePreludeDts,
  tools: [],
  sampleInput: {
    ticket: {
      id: "topic_abc123",
      accountId: "acct_123",
      status: "CLOSED",
      priority: null,
      assigneeType: "USER",
      mongoAssigneeId: "65b0c0ffee0000000000abcd",
      mongoCustomerId: "65b0c0ffee0000000000ef01",
      ticketNumber: 1042,
      closureReason: "resolved",
      readAt: "2026-06-18T12:00:00.000Z",
      lastCustomerMessageAt: "2026-06-18T11:30:00.000Z",
      lastMerchantMessageAt: "2026-06-18T11:55:00.000Z",
      statusUpdatedAt: "2026-06-18T12:00:00.000Z",
      snoozedUntil: null,
    },
    threads: [
      {
        id: "thread_1",
        channel: "email",
        integrationType: "gmail",
        integrationId: "integration_1",
        externalThreadId: "gmail-thread-xyz",
        createdAt: "2026-06-18T10:00:00.000Z",
        emailSubject: "Where is my order?",
      },
    ],
    channels: { integrationTypes: ["gmail"], labels: ["support"] },
    trigger: {
      triggerType: "CONVERSATION_STATUS_CHANGED",
      triggeredAt: "2026-06-18T12:00:00.000Z",
      subjectEntityId: "topic_abc123",
    },
  } satisfies z.infer<typeof supportCustomActionInputSchema>,
};
